import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PageHeader } from "@/mockups/ui";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, Trash2, Clock, Users } from "lucide-react";
import { fmtHM, fmtMinHM } from "@/lib/utils";

const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("");

function localISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function weekStart(offset) {
  const d = new Date();
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7) + offset * 7);
  d.setHours(0, 0, 0, 0);
  return d;
}
const DAY_NAMES = ["lun", "mar", "mié", "jue", "vie", "sáb", "dom"];

function EntryDialog({ open, onOpenChange, projects, onSaved }) {
  const [form, setForm] = useState({ project_id: "", milestone_id: "none", date: localISO(new Date()), hours: "1", mins: "0", description: "" });
  const [milestones, setMilestones] = useState([]);
  const [loadingMs, setLoadingMs] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Al cambiar de proyecto, cargar sus hitos y resetear la selección
  useEffect(() => {
    if (!form.project_id) { setMilestones([]); return; }
    setLoadingMs(true);
    setForm((f) => ({ ...f, milestone_id: "none" }));
    api.get(`/projects/${form.project_id}`)
      .then((r) => setMilestones(r.data.milestones || []))
      .catch(() => setMilestones([]))
      .finally(() => setLoadingMs(false));
  }, [form.project_id]);

  const save = async () => {
    if (!form.project_id) { toast.error("Selecciona un proyecto"); return; }
    const minutes = (parseInt(form.hours, 10) || 0) * 60 + (parseInt(form.mins, 10) || 0);
    if (!minutes || minutes <= 0) { toast.error("Introduce horas válidas"); return; }
    setSaving(true);
    try {
      await api.post("/time-entries", {
        project_id: form.project_id,
        milestone_id: form.milestone_id === "none" ? null : form.milestone_id,
        date: form.date, minutes, description: form.description,
      });
      toast.success("Horas registradas");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Registrar horas</DialogTitle><DialogDescription className="sr-only">Formulario de registro de horas</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Proyecto *</Label>
            <Select value={form.project_id} onValueChange={(v) => set("project_id", v)}>
              <SelectTrigger data-testid="entry-project-select"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
              <SelectContent>{(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Hito {loadingMs && <span className="text-xs text-muted-foreground">· cargando…</span>}</Label>
            <Select value={form.milestone_id} onValueChange={(v) => set("milestone_id", v)} disabled={!form.project_id || milestones.length === 0}>
              <SelectTrigger data-testid="entry-milestone-select"><SelectValue placeholder={form.project_id ? "Sin hito" : "Elige un proyecto primero"} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin hito (proyecto general)</SelectItem>
                {milestones.map((m) => <SelectItem key={m.id} value={m.id} disabled={!!m.closed} data-testid={`entry-milestone-opt-${m.id}`}>{m.name}{m.closed ? " (cerrado)" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-[1fr_auto] gap-4">
            <div className="space-y-1.5"><Label>Fecha</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="entry-date-input" /></div>
            <div className="space-y-1.5">
              <Label>Duración</Label>
              <div className="flex items-center gap-1.5">
                <Input type="number" min="0" step="1" className="w-16 tabular-nums" value={form.hours} onChange={(e) => set("hours", e.target.value)} data-testid="entry-hours-input" />
                <span className="text-sm text-muted-foreground">h</span>
                <Input type="number" min="0" max="59" step="5" className="w-16 tabular-nums" value={form.mins} onChange={(e) => set("mins", e.target.value)} data-testid="entry-minutes-input" />
                <span className="text-sm text-muted-foreground">min</span>
              </div>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Descripción</Label><Input value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="¿En qué has trabajado?" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="entry-save-btn">{saving ? "Guardando…" : "Registrar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Horas() {
  const { can, user } = useAuth();
  const canSeeAll = user?.role === "Administrador" || user?.role === "Directora";
  const [offset, setOffset] = useState(0);
  const [personId, setPersonId] = useState("all");
  const start = weekStart(offset);
  const end = new Date(start); end.setDate(end.getDate() + 6);
  const params = { date_from: localISO(start), date_to: localISO(end) };
  if (canSeeAll && personId !== "all") params.user_id = personId;
  const { data: entries, loading, refetch } = useFetch("/time-entries", params);
  const { data: projects } = useFetch("/projects");
  const { data: team } = useFetch("/team");
  const [dialogOpen, setDialogOpen] = useState(false);

  const total = (entries || []).reduce((a, e) => a + e.minutes, 0) / 60;

  // Desglose por persona (solo managers viendo "Todos")
  const byPerson = {};
  (entries || []).forEach((e) => {
    const k = e.user_id || "—";
    if (!byPerson[k]) byPerson[k] = { name: e.user_name || "—", picture: e.user_picture, minutes: 0 };
    byPerson[k].minutes += e.minutes;
  });
  const people = Object.entries(byPerson).sort((a, b) => b[1].minutes - a[1].minutes);

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try { await api.delete(`/time-entries/${id}`); toast.success("Eliminado"); refetch(); }
    catch (e) { toast.error("Error"); }
  };

  // group by date
  const byDate = {};
  (entries || []).forEach((e) => { (byDate[e.date] = byDate[e.date] || []).push(e); });
  const dates = Object.keys(byDate).sort().reverse();

  const fmtRange = () => `${start.toLocaleDateString("es-ES", { day: "2-digit", month: "short" })} – ${end.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })}`;

  // Días de la semana visible (siempre 7, aunque no haya horas)
  const todayISO = localISO(new Date());
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start); d.setDate(d.getDate() + i); return d;
  });

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader title="Registro de horas" subtitle={canSeeAll ? "Consulta las horas dedicadas por todo el equipo y registra las tuyas" : "Consulta y registra las horas dedicadas a cada proyecto"}>
        {can("timesheets", "create") && <Button size="sm" className="gap-1.5" onClick={() => setDialogOpen(true)} data-testid="add-entry-btn"><Plus className="h-4 w-4" /> Registrar horas</Button>}
      </PageHeader>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setOffset((o) => o - 1)} data-testid="prev-week"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-sm font-medium px-3">{fmtRange()}</span>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setOffset((o) => o + 1)} data-testid="next-week"><ChevronRight className="h-4 w-4" /></Button>
            {offset !== 0 && <Button variant="ghost" size="sm" onClick={() => setOffset(0)}>Hoy</Button>}
          </div>
          {canSeeAll && (
            <Select value={personId} onValueChange={setPersonId}>
              <SelectTrigger className="w-[210px] h-8" data-testid="person-filter"><Users className="h-4 w-4 mr-1.5 text-muted-foreground" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todo el equipo</SelectItem>
                {(team || []).map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="text-sm text-muted-foreground">Total: <span className="font-heading font-bold text-foreground tabular-nums" data-testid="week-total">{fmtHM(total)}</span></div>
      </div>

      {canSeeAll && personId === "all" && people.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="person-breakdown">
          {people.map(([uid, p]) => (
            <button key={uid} onClick={() => setPersonId(uid)} className="flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs hover:bg-muted transition-colors" data-testid={`person-chip-${uid}`}>
              <Avatar className="h-5 w-5"><AvatarImage src={p.picture} /><AvatarFallback className="text-[8px] bg-muted">{initials(p.name)}</AvatarFallback></Avatar>
              <span className="font-medium">{p.name}</span>
              <span className="tabular-nums text-primary font-semibold">{fmtMinHM(p.minutes)}</span>
            </button>
          ))}
        </div>
      )}

      {/* Calendario semanal (siempre visible, aunque no haya horas) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="week-calendar">
        {weekDays.map((d, i) => {
          const iso = localISO(d);
          const list = byDate[iso] || [];
          const dayTotal = list.reduce((a, e) => a + e.minutes, 0) / 60;
          const isToday = iso === todayISO;
          const isWeekend = i >= 5;
          return (
            <Card key={iso} data-testid={`week-day-${iso}`} className={`border-border shadow-none overflow-hidden flex flex-col min-h-[128px] ${isToday ? "ring-2 ring-primary/60" : ""} ${isWeekend ? "bg-muted/30" : ""}`}>
              <div className={`flex items-center justify-between px-3 py-2 border-b border-border ${isToday ? "bg-primary/10" : "bg-muted/40"}`}>
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{DAY_NAMES[i]}</span>
                  <span className={`text-sm font-heading font-bold tabular-nums ${isToday ? "text-primary" : ""}`}>{d.getDate()}</span>
                </div>
                {dayTotal > 0 && <span className="text-xs font-semibold tabular-nums text-primary">{fmtHM(dayTotal)}</span>}
              </div>
              <div className="flex-1 p-2 space-y-1.5 overflow-y-auto max-h-40">
                {list.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-[11px] text-muted-foreground/60">—</div>
                ) : (
                  list.map((e) => (
                    <div key={e.id} className="rounded-md bg-muted/50 px-2 py-1" data-testid={`week-entry-${e.id}`}>
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-[11px] font-medium truncate">{e.project_name || "—"}</span>
                        <span className="text-[11px] tabular-nums font-semibold shrink-0">{fmtMinHM(e.minutes)}</span>
                      </div>
                      {canSeeAll && e.user_name && <div className="text-[10px] text-muted-foreground truncate">{e.user_name}</div>}
                    </div>
                  ))
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {loading ? <div className="py-16 flex justify-center"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
        : dates.length === 0 ? <Card className="border-border shadow-none py-16 text-center text-sm text-muted-foreground"><Clock className="h-8 w-8 mx-auto mb-3 text-muted-foreground/40" />No hay horas registradas en este periodo.</Card>
        : (
        <div className="space-y-4">
          {dates.map((d) => {
            const dayTotal = byDate[d].reduce((a, e) => a + e.minutes, 0) / 60;
            const dateObj = new Date(d);
            return (
              <Card key={d} className="border-border shadow-none overflow-hidden">
                <div className="flex items-center justify-between px-5 py-2.5 bg-muted/40 border-b border-border">
                  <span className="text-sm font-semibold">{DAY_NAMES[(dateObj.getDay() + 6) % 7]} · {dateObj.toLocaleDateString("es-ES", { day: "2-digit", month: "long" })}</span>
                  <span className="text-sm font-heading font-bold tabular-nums">{fmtHM(dayTotal)}</span>
                </div>
                <div className="divide-y divide-border">
                  {byDate[d].map((e) => (
                    <div key={e.id} className="flex items-center gap-4 px-5 py-3" data-testid={`entry-${e.id}`}>
                      {canSeeAll && (
                        <div className="flex items-center gap-2 w-40 shrink-0" title={e.user_name}>
                          <Avatar className="h-7 w-7"><AvatarImage src={e.user_picture} /><AvatarFallback className="text-[10px] bg-muted">{initials(e.user_name)}</AvatarFallback></Avatar>
                          <span className="text-sm font-medium truncate">{e.user_name || "—"}</span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{e.project_name || "—"}{e.milestone_name && <span className="ml-2 text-[11px] font-normal text-muted-foreground">▸ {e.milestone_name}</span>}</div>
                        <div className="text-xs text-muted-foreground">{e.description || "Sin descripción"}</div>
                      </div>
                      <span className="font-heading font-bold text-sm tabular-nums">{fmtMinHM(e.minutes)}</span>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => del(e.id)} data-testid={`entry-del-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {dialogOpen && <EntryDialog open={dialogOpen} onOpenChange={setDialogOpen} projects={projects} onSaved={refetch} />}
    </div>
  );
}
